/* eslint-disable camelcase, max-len, @typescript-eslint/naming-convention */
/**
 * New Relic Browser monitoring bootstrap.
 *
 * Pairs with the existing APM setup on the API side. Configuration comes from
 * `REACT_APP_NEW_RELIC_BROWSER_*` env vars injected at build time by the Azure
 * DevOps pipeline (CRA inlines REACT_APP_* into the bundle).
 *
 * If any required value is missing the agent is NOT registered — keeps local
 * `npm start` and PR builds quiet without polluting NR with junk data.
 *
 * Lint disables above are intentional: NR's BrowserAgent options use snake_case
 * keys (`session_replay`, `mask_all_inputs`, …) which airbnb-typescript flags.
 */

import { BrowserAgent } from '@newrelic/browser-agent/loaders/browser-agent';

const {
  REACT_APP_NEW_RELIC_BROWSER_LICENSE_KEY: licenseKey,
  REACT_APP_NEW_RELIC_BROWSER_APPLICATION_ID: applicationID,
  REACT_APP_NEW_RELIC_ACCOUNT_ID: accountID,
  // Trust key defaults to accountID for single-org NR accounts.
  REACT_APP_NEW_RELIC_TRUST_KEY: trustKey,
  // Agent ID defaults to applicationID for single-app SPA agents.
  REACT_APP_NEW_RELIC_BROWSER_AGENT_ID: agentID,
  REACT_APP_STAGE: stage,
} = process.env;

// The NR Browser entity is production-only; the same app ID is built into
// every environment, so gate registration to prod to keep uat/sit out of it.
const isProduction = stage === 'prd';

if (isProduction && licenseKey && applicationID && accountID) {
  // eslint-disable-next-line no-new
  new BrowserAgent({
    init: {
      // SPA: tie subsequent route changes to a single page-view session.
      distributed_tracing: { enabled: true },
      privacy: { cookies_enabled: true },
      ajax: { deny_list: ['bam.nr-data.net'] },
      // Session Replay (enabled in NR UI). Captures DOM mutations for replay.
      // session_trace is required alongside it.
      session_trace: { enabled: true },
      session_replay: {
        enabled: true,
        // 10% of normal sessions, 100% of sessions with a JS/AJAX error.
        sampling_rate: 20.0,
        error_sampling_rate: 100.0,
        // Privacy-safe defaults: mask everything; opt elements back in by
        // tagging them with `class="nr-unmask"`. Use `class="nr-mask"` /
        // `class="nr-block"` to add extra masking/blocking on top.
        mask_all_inputs: true,
        mask_text_selector: '*',
        block_selector: '',
        collect_fonts: true,
        inline_images: false,
      },
    },
    info: {
      beacon: 'bam.nr-data.net',
      errorBeacon: 'bam.nr-data.net',
      licenseKey,
      applicationID,
      sa: 1,
    },
    loader_config: {
      accountID,
      trustKey: trustKey || accountID,
      agentID: agentID || applicationID,
      licenseKey,
      applicationID,
    },
  });

  if (stage) {
    // Tag every event with the deploy environment for filtering in NR.
    const nr = (window as unknown as { newrelic?: { setCustomAttribute: (k: string, v: string) => void } }).newrelic;

    nr?.setCustomAttribute('environment', stage);
  }
}

export {};
